import {
  LinkableRecordTypesEnum,
  RecordLinkTypeEnum,
} from 'components/framework/Enums';
import {
  Epic,
  Feature,
  Goal,
  Idea,
  IdeaTheme,
  Initiative,
  KeyResult,
  Page,
  PdlcStep,
  Project,
  RecordLink,
  Release,
  ReleasePhase,
  Requirement,
  Task,
} from 'components/framework/models';
import { uniqBy } from 'lodash';
import type {
  WhiteboardRecordTypeNames,
  RecordId,
  WhiteboardRecordTypes,
} from 'javascripts/shared-types';
import type {
  HierarchyImportRelationshipType,
  RelatedRecordManager as IRelatedRecordManager,
  FetchOptions,
  HierarchyNode,
  HierarchyResult,
} from '@aha-app/whiteboard';

interface RecordWithLinks {
  [key: string]: any;
  childRecordLinks?: Array<{
    parentRecordId?: RecordId;
    parentRecordType?: WhiteboardRecordTypeNames;
    linkType?: string;
    relationDescription?: string;
  }>;
  parentRecordLinks?: Array<{
    childRecordId?: RecordId;
    childRecordType?: WhiteboardRecordTypeNames;
    linkType?: string;
    relationDescription?: string;
  }>;
}

interface FetchedRecord {
  recordId: RecordId;
  recordType: WhiteboardRecordTypes;
  linkType?: string;
  relationDescription?: string;
}

const MODELS = {
  epics: Epic,
  features: Feature,
  goals: Goal,
  ideas: Idea,
  ideaThemes: IdeaTheme,
  initiatives: Initiative,
  keyResults: KeyResult,
  pages: Page,
  pdlcSteps: PdlcStep,
  Project: Project,
  releases: Release,
  releasePhases: ReleasePhase,
  requirements: Requirement,
  tasks: Task,
} as const;

const recordTypenamesToType = {
  Epic: 'epics',
  Feature: 'features',
  Goal: 'goals',
  Idea: 'ideas',
  IdeaTheme: 'ideaThemes',
  Initiative: 'initiatives',
  KeyResult: 'keyResults',
  Page: 'pages',
  PdlcStep: 'pdlcSteps',
  Project: 'Project',
  Release: 'releases',
  ReleasePhase: 'releasePhases',
  Requirement: 'requirements',
  Task: 'tasks',
} as const;

type ModelType = keyof typeof MODELS;

interface HierarchyBuildContext {
  hierarchyResult: HierarchyResult;
  options: FetchOptions;
  relationshipType: HierarchyImportRelationshipType;
  parentId?: string;
  depth: number;
  linkType?: string;
  relationDescription?: string;
}

const getRelationDescription = (link, reverseRelationship) => {
  switch (link.linkType) {
    case RecordLinkTypeEnum.DEPENDS_ON.value:
      return link.relationDescription;
    case RecordLinkTypeEnum.IMPACTS.value:
      return reverseRelationship ? 'Is impacted by' : 'Impacts';
    case RecordLinkTypeEnum.BLOCKS.value:
      return reverseRelationship ? 'Is blocked by' : 'Blocks';
    case RecordLinkTypeEnum.RELATES_TO.value:
      return link.relationDescription;
    case RecordLinkTypeEnum.CONTAINS.value:
      return reverseRelationship ? 'Is contained by' : 'Contains';
    default:
      return link.relationDescription || 'Related';
  }
};

const extractRecordReferencesFromLinks = (
  links:
    | RecordWithLinks['childRecordLinks']
    | RecordWithLinks['parentRecordLinks'],
  reverse = false
): FetchedRecord[] => {
  const results: FetchedRecord[] = [];
  if (!Array.isArray(links) || links.length === 0) {
    return [];
  }
  const isParentLinks = links.some(link => link.childRecordId);

  if (Array.isArray(links)) {
    links.forEach(link => {
      const recordId = isParentLinks ? link.childRecordId : link.parentRecordId;
      const recordType = isParentLinks
        ? link.childRecordType
        : link.parentRecordType;

      // If we extracted this link from childRecordLinks, that means te selected
      // record is the parent
      const relationDescription = getRelationDescription(link, reverse);

      results.push({
        recordId: recordId,
        recordType: recordType,
        linkType: link.linkType,
        relationDescription,
      });
    });
  }

  return results;
};

const normalizeRecordType = (type: string): WhiteboardRecordTypes => {
  if (type.includes('Ideas::')) {
    type = type.replace(/^Ideas::/, '');
  }
  if (type === 'StrategicImperative') {
    type = 'Goal';
  }
  return type as WhiteboardRecordTypes;
};

const normalizeRelatedRecords = (relatedRecords: FetchedRecord[]): void => {
  relatedRecords.forEach(record => {
    record.recordType = normalizeRecordType(record.recordType);
  });
};

class RelatedRecordManager implements IRelatedRecordManager {
  // This is meant as a common sense limit to prevent importing massive hierarchies
  // We may change this based on feedback from PM or customers
  private static readonly MAX_HIERARCHY_DEPTH = 15;

  // For now, we are not supporting projects, as we need to carefully consider
  // what exactly we want to import for projects.
  supportedRecordTypes: string[] = Object.keys(MODELS).filter(
    key => !['Project', 'releasePhases'].includes(key)
  );

  private getParentFields(recordType: string): string[] {
    switch (recordType) {
      case 'features':
        return ['epic', 'initiative', 'goals'];
      case 'requirements':
        return ['feature'];
      case 'epics':
        return ['initiative', 'goals'];
      case 'initiatives':
        return ['goals', 'project'];
      case 'goals':
        return ['project'];
      // TODO add support in followup work
      // case 'releasePhases':
      //   return ['release'];
      case 'pdlcSteps':
        return ['pdlcFramework'];
      case 'keyResults':
        return ['goal'];
      case 'ideas':
        return ['ideaTheme'];
      case 'ideaThemes':
        return ['project'];
      case 'pages':
        return ['project'];
      case 'releases':
        return ['project'];
      case 'tasks':
        return ['project', 'requirement', 'feature', 'epic', 'initiative'];
      case 'Project':
        return [];
      default:
        return [];
    }
  }

  private getChildFields(recordType: string): string[] {
    switch (recordType) {
      case 'features':
        return ['requirements', 'tasks'];
      case 'epics':
        return ['features', 'tasks'];
      case 'initiatives':
        return ['epics', 'features', 'tasks'];
      case 'goals':
        return ['initiatives', 'epics', 'features', 'keyResults', 'tasks'];
      case 'releases':
        return ['releasePhases'];
      case 'pdlcFrameworks':
        return ['pdlcSteps'];
      case 'Project':
        return [
          'initiatives',
          'epics',
          'features',
          'requirements',
          'goals',
          'pages',
          'ideaThemes',
          'tasks',
          'releases',
          'projects',
        ];
      case 'ideaThemes':
        return ['ideas'];
      case 'requirements':
        return ['tasks'];
      default:
        return [];
    }
  }

  private getDependencyLinkTypes(
    relationshipType: HierarchyImportRelationshipType
  ): string[] {
    switch (relationshipType) {
      case 'dependencies':
        return [
          RecordLinkTypeEnum.DEPENDS_ON.value,
          RecordLinkTypeEnum.IMPACTS.value,
          RecordLinkTypeEnum.BLOCKS.value,
        ];
      case 'related':
        return [RecordLinkTypeEnum.RELATES_TO.value];
      default:
        return [];
    }
  }

  async fetchHierarchy(
    recordId: RecordId,
    recordType: ModelType,
    relationshipType: HierarchyImportRelationshipType,
    options: FetchOptions = {
      maxRecords: 1000,
      alreadyFetched: new Set<string>(),
    }
  ): Promise<HierarchyResult> {
    this.validateHierarchyInput(recordId, recordType, relationshipType);

    const hierarchyResult: HierarchyResult = {
      nodes: {},
      originalRecordId: recordId,
    };

    hierarchyResult.nodes[recordId] = {
      record: { recordId, recordType: recordType as WhiteboardRecordTypeNames },
      parentIds: [],
      childIds: [],
      depth: 0,
      relationshipType,
    };

    const context: HierarchyBuildContext = {
      hierarchyResult,
      options,
      relationshipType,
      depth: 0,
    };

    await this.buildHierarchy(recordId, recordType, context);

    return hierarchyResult;
  }

  private async buildHierarchy(
    recordId: string,
    recordType: ModelType,
    context: HierarchyBuildContext
  ): Promise<void> {
    if (!this.shouldContinueTraversal(context, recordId)) {
      return;
    }

    this.createOrUpdateNode(recordId, recordType, context);
    context.options.alreadyFetched.add(recordId);

    const relatedRecords = await this.fetchRelatedRecordsOfType(
      recordId,
      recordType,
      context.relationshipType
    );

    await this.processChildRecords(recordId, relatedRecords, context);
  }

  private shouldContinueTraversal(
    context: HierarchyBuildContext,
    recordId: string
  ): boolean {
    if (context.options.alreadyFetched.size >= context.options.maxRecords) {
      return false;
    }

    if (context.depth >= RelatedRecordManager.MAX_HIERARCHY_DEPTH) {
      console.warn(
        `Maximum hierarchy depth (${RelatedRecordManager.MAX_HIERARCHY_DEPTH}) reached for record ${recordId}`
      );
      return false;
    }

    return true;
  }

  private createOrUpdateNode(
    recordId: string,
    recordType: ModelType,
    context: HierarchyBuildContext
  ): HierarchyNode {
    const currentNode = context.hierarchyResult.nodes[recordId] || {
      record: { recordId, recordType },
      parentIds: [],
      childIds: [],
      depth: context.depth,
      relationshipType: context.relationshipType,
      linkType: context.linkType,
      relationDescription: context.relationDescription,
    };

    if (context.parentId) {
      const parentNode = context.hierarchyResult.nodes[context.parentId];
      this.updateNodeRelationships(
        context.hierarchyResult,
        parentNode,
        currentNode
      );
    }

    context.hierarchyResult.nodes[recordId] = currentNode;
    return currentNode;
  }

  private async processChildRecords(
    parentRecordId: string,
    relatedRecords: FetchedRecord[],
    context: HierarchyBuildContext
  ): Promise<void> {
    for (const relatedRecord of relatedRecords) {
      if (context.options.alreadyFetched.has(relatedRecord.recordId)) {
        this.handleExistingNodeRelationship(
          parentRecordId,
          relatedRecord,
          context
        );
        continue;
      }

      const childContext = this.createChildContext(
        parentRecordId,
        relatedRecord,
        context
      );
      await this.buildHierarchy(
        relatedRecord.recordId,
        recordTypenamesToType[relatedRecord.recordType],
        childContext
      );
    }
  }

  private handleExistingNodeRelationship(
    parentRecordId: string,
    relatedRecord: FetchedRecord,
    context: HierarchyBuildContext
  ): void {
    const parentNode = context.hierarchyResult.nodes[parentRecordId];
    const childNode = context.hierarchyResult.nodes[relatedRecord.recordId];
    this.updateNodeRelationships(
      context.hierarchyResult,
      parentNode,
      childNode
    );
  }

  private createChildContext(
    parentRecordId: string,
    relatedRecord: FetchedRecord,
    context: HierarchyBuildContext
  ): HierarchyBuildContext {
    return {
      ...context,
      parentId: parentRecordId,
      depth: context.depth + 1,
      linkType: relatedRecord.linkType,
      relationDescription: relatedRecord.relationDescription,
    };
  }

  private validateHierarchyInput(
    recordId: string,
    recordType: ModelType,
    relationshipType: HierarchyImportRelationshipType
  ): void {
    if (!recordId) {
      throw new Error('recordId is required');
    }
    if (!this.supportedRecordTypes.includes(recordType)) {
      throw new Error(
        `Unsupported record type: ${recordType}. Supported types: ${this.supportedRecordTypes.join(
          ', '
        )}`
      );
    }
    const validRelationshipTypes = [
      'parents',
      'children',
      'dependencies',
      'related',
    ];
    if (!validRelationshipTypes.includes(relationshipType)) {
      throw new Error(
        `Invalid relationship type: ${relationshipType}. Valid types: ${validRelationshipTypes.join(
          ', '
        )}`
      );
    }
  }

  private updateNodeRelationships(
    hierarchyResult: HierarchyResult,
    parentNode: HierarchyNode,
    childNode: HierarchyNode
  ): void {
    const parentId = parentNode.record.recordId;
    const childId = childNode.record.recordId;

    if (!childNode.parentIds.includes(parentId)) {
      childNode.parentIds.push(parentId);
    }

    if (!parentNode.childIds.includes(childId)) {
      parentNode.childIds.push(childId);
    }
  }

  async fetchRelatedRecordsOfType(
    recordId: string,
    recordType: keyof typeof MODELS,
    relationshipType: HierarchyImportRelationshipType
  ): Promise<FetchedRecord[]> {
    const modelClass = MODELS[recordType];
    if (!modelClass) {
      throw new Error(
        `Invalid record type: ${recordType}. Supported types are: ${Object.keys(
          MODELS
        ).join(', ')}`
      );
    }
    let relatedRecords: FetchedRecord[] = [];
    switch (relationshipType) {
      case 'parents':
        relatedRecords = await this.fetchParentRelationships(
          recordId,
          recordType,
          modelClass
        );
        break;
      case 'children':
        relatedRecords = await this.fetchChildRelationships(
          recordId,
          recordType,
          modelClass
        );
        break;
      case 'dependencies':
      case 'related':
        relatedRecords = await this.fetchDependencyRelationships(
          recordId,
          modelClass,
          relationshipType
        );
        break;
      default:
        throw new Error(`Invalid relationship type: ${relationshipType}`);
    }

    normalizeRelatedRecords(relatedRecords);
    return relatedRecords;
  }

  private async fetchParentRelationships(
    recordId: string,
    recordType: keyof typeof MODELS,
    modelClass: (typeof MODELS)[keyof typeof MODELS]
  ): Promise<FetchedRecord[]> {
    const parentFields = this.getParentFields(recordType);
    if (parentFields.length === 0) {
      return [];
    }

    try {
      const data = (await modelClass
        .merge(Object.fromEntries(parentFields.map(field => [field, ['id']])))
        .merge({
          childRecordLinks: RecordLink.select(
            'parentRecordId',
            'parentRecordType',
            'linkType',
            'relationDescription'
          ).where({
            linkType: [RecordLinkTypeEnum.CONTAINS.value],
            recordType: LinkableRecordTypesEnum.values,
          }),
        })
        .find(recordId)) as RecordWithLinks;

      const results: FetchedRecord[] = [];

      if (data) {
        parentFields.forEach(field => {
          const parent = data[field];
          if (!parent) return;

          // Pull the parent fields directly
          const parents = Array.isArray(parent) ? parent : [parent];
          parents.forEach(p => {
            if (p.id && p.typename) {
              results.push({
                recordId: p.id,
                recordType: p.typename,
                linkType: RecordLinkTypeEnum.CONTAINS.value,
                relationDescription: 'Contains',
              });
            }
          });
        });

        // And then pass through the CONTAINS record links
        const childLinks =
          'childRecordLinks' in data ? data.childRecordLinks : undefined;
        results.push(...extractRecordReferencesFromLinks(childLinks));
      }

      return uniqBy(results, 'recordId');
    } catch (error) {
      console.error(`Error fetching parent relationships:`, error);
      return [];
    }
  }

  private async fetchChildRelationships(
    recordId: string,
    recordType: keyof typeof MODELS,
    modelClass: (typeof MODELS)[keyof typeof MODELS]
  ): Promise<FetchedRecord[]> {
    const childFields = this.getChildFields(recordType);
    if (childFields.length === 0) {
      return [];
    }

    try {
      const data = (await modelClass
        .merge(Object.fromEntries(childFields.map(field => [field, ['id']])))
        .merge({
          parentRecordLinks: RecordLink.select(
            'childRecordId',
            'childRecordType',
            'linkType',
            'relationDescription'
          ).where({
            linkType: [RecordLinkTypeEnum.CONTAINS.value],
            recordType: LinkableRecordTypesEnum.values,
          }),
        })
        .find(recordId)) as RecordWithLinks;

      const results: FetchedRecord[] = [];

      if (data) {
        childFields.forEach(field => {
          const children = data[field];
          if (!children) return;

          // Pull the child fields directly
          const childrenArray = Array.isArray(children) ? children : [children];
          childrenArray.forEach(child => {
            if (child.id && child.typename) {
              results.push({
                recordId: child.id,
                recordType: child.typename,
                linkType: RecordLinkTypeEnum.CONTAINS.value,
                relationDescription: 'Contains',
              });
            }
          });
        });

        const parentLinks =
          'parentRecordLinks' in data ? data.parentRecordLinks : undefined;
        results.push(...extractRecordReferencesFromLinks(parentLinks));
      }

      return uniqBy(results, 'recordId');
    } catch (error) {
      console.error(`Error fetching child relationships:`, error);
      return [];
    }
  }

  private async fetchDependencyRelationships(
    recordId: string,
    modelClass: (typeof MODELS)[keyof typeof MODELS],
    relationshipType: HierarchyImportRelationshipType
  ): Promise<FetchedRecord[]> {
    const recordLinkTypes = this.getDependencyLinkTypes(relationshipType);
    if (recordLinkTypes.length === 0) {
      return [];
    }

    try {
      const data = (await modelClass
        .merge({
          parentRecordLinks: RecordLink.select(
            'childRecordId',
            'childRecordType',
            'linkType',
            'relationDescription'
          ).where({
            linkType: recordLinkTypes,
            recordType: LinkableRecordTypesEnum.values,
          }),
          childRecordLinks: RecordLink.select(
            'parentRecordId',
            'parentRecordType',
            'linkType',
            'relationDescription'
          ).where({
            linkType: recordLinkTypes,
            recordType: LinkableRecordTypesEnum.values,
          }),
        })
        .find(recordId)) as RecordWithLinks;

      const results: FetchedRecord[] = [];

      if (data) {
        const parentLinks =
          'parentRecordLinks' in data ? data.parentRecordLinks : undefined;
        results.push(...extractRecordReferencesFromLinks(parentLinks));

        const childLinks =
          'childRecordLinks' in data ? data.childRecordLinks : undefined;
        results.push(...extractRecordReferencesFromLinks(childLinks, true));
      }

      return uniqBy(results, 'recordId');
    } catch (error) {
      console.error(`Error fetching ${relationshipType} record links:`, error);
      return [];
    }
  }
}

export default RelatedRecordManager;
